/**
 * CallDetailSidebar
 *
 * Call metadata card: status, direction, sentiment, outcome, trust level,
 * performance metrics, and cost breakdown.
 */

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import type { ICallDetailSidebarProps, TCallSentiment, TCallOutcome } from '@/@types/voiceAgents';

function formatDuration(ms: number): string {
  const totalSec = Math.round(ms / 1000);
  const min = Math.floor(totalSec / 60);
  const sec = totalSec % 60;
  return `${min}:${String(sec).padStart(2, '0')}`;
}

function formatCost(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

const SENTIMENT_VARIANTS: Record<TCallSentiment, 'default' | 'secondary' | 'destructive'> = {
  positive: 'default',
  neutral: 'secondary',
  negative: 'destructive',
};

const OUTCOME_VARIANTS: Record<TCallOutcome, 'default' | 'secondary' | 'outline'> = {
  resolved: 'default',
  escalated: 'secondary',
  callback_needed: 'outline',
  no_action: 'secondary',
};

export function CallDetailSidebar({ call, agentName }: ICallDetailSidebarProps) {
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Call Details</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4 text-sm">
        {/* Status & Direction */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">Status</span>
            <Badge variant="outline" className="capitalize">
              {call.status}
            </Badge>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">Direction</span>
            <Badge variant="outline" className="capitalize">
              {call.direction}
            </Badge>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">Agent</span>
            <span className="font-medium">{agentName}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">Duration</span>
            <span>{call.durationMs ? formatDuration(call.durationMs) : '—'}</span>
          </div>
        </div>

        <Separator />

        {/* Sentiment & Outcome */}
        <div className="space-y-2">
          {call.sentiment && (
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Sentiment</span>
              <Badge variant={SENTIMENT_VARIANTS[call.sentiment]} className="capitalize">
                {call.sentiment}
              </Badge>
            </div>
          )}
          {call.outcome && (
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Outcome</span>
              <Badge variant={OUTCOME_VARIANTS[call.outcome]} className="capitalize">
                {call.outcome.replace('_', ' ')}
              </Badge>
            </div>
          )}
        </div>

        {/* AI Summary */}
        {call.outcomeSummary && (
          <>
            <Separator />
            <div>
              <p className="text-muted-foreground mb-1">AI Summary</p>
              <p className="text-sm leading-relaxed">{call.outcomeSummary}</p>
            </div>
          </>
        )}

        {/* Trust Level */}
        <Separator />
        <div className="flex items-center justify-between">
          <span className="text-muted-foreground">Trust Level</span>
          <Badge variant="outline" className="capitalize">
            {call.callerTrustLevel}
          </Badge>
        </div>

        {/* Performance */}
        <Separator />
        <div className="space-y-2">
          <p className="text-muted-foreground font-medium">Performance</p>
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">TTFA</span>
            <span>
              {call.avgTimeToFirstAudioMs ? `${Math.round(call.avgTimeToFirstAudioMs)}ms` : '—'}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">Interruptions</span>
            <span>{call.interruptionCount}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">Fallbacks</span>
            <span>{call.fallbackActivations}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">Total Turns</span>
            <span>{call.totalTurns}</span>
          </div>
          {call.tokenUsage !== undefined && (
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Tokens</span>
              <span>{call.tokenUsage.toLocaleString()}</span>
            </div>
          )}
        </div>

        {/* Cost Breakdown */}
        {call.cost && (
          <>
            <Separator />
            <div className="space-y-2">
              <p className="text-muted-foreground font-medium">Cost Breakdown</p>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">STT</span>
                <span>{formatCost(call.cost.sttCost)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">LLM</span>
                <span>{formatCost(call.cost.llmCost)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">TTS</span>
                <span>{formatCost(call.cost.ttsCost)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">SIP</span>
                <span>{formatCost(call.cost.sipCost)}</span>
              </div>
              <div className="flex items-center justify-between font-medium">
                <span>Total</span>
                <span>{formatCost(call.cost.totalCost)}</span>
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
